<template>
  <div class="nlp-page">
    <!-- ── 채팅 본문 ── -->
        <div class="nlp-body" ref="chatBody">
          <div
            v-for="(msg, idx) in messages"
            :key="idx"
            class="nlp-row"
            :class="msg.role"
          >
            <!-- AI 아바타 -->
            <div v-if="msg.role === 'ai'" class="nlp-avatar">
              <v-icon icon="mdi-robot-outline" color="white" size="15" />
            </div>

            <!-- 버블 -->
            <div class="nlp-bubble" :class="msg.role">
              <!-- 일반 텍스트 -->
              <template v-if="msg.type === 'text'">
                <span class="nlp-text" v-html="renderAiText(msg.text)"></span>
              </template>

              <!-- 미리보기 카드 -->
              <template v-else-if="msg.type === 'preview'">
                <div
                  class="preview-card"
                  :class="msg.data.valid ? 'valid' : 'invalid'"
                >
                  <!-- ─── updateUser intent — 유저 정보 변경 미리보기 (multi-field) ─── -->
                  <template
                    v-if="msg.data.intent === 'updateUser' && msg.data.parsed"
                  >
                    <div class="preview-hero hero-ok">
                      <div class="preview-hero__head">
                        <span class="preview-hero__badge">
                          <v-icon icon="mdi-account-edit" size="16" />
                          유저 정보 변경
                        </span>
                      </div>
                      <div class="preview-hero__total">
                        <span
                          class="preview-hero__unit"
                          style="
                            font-size: var(--font-h3-size);
                            color: var(--text-primary);
                            font-weight: 700;
                          "
                        >
                          {{ updateSummaryLabel(msg.data.parsed) }}
                        </span>
                      </div>
                      <div class="preview-hero__breakdown">
                        <span
                          ><v-icon icon="mdi-identifier" size="13" />{{
                            msg.data.parsed.userId
                          }}</span
                        >
                        <template v-if="msg.data.parsed.current?.name">
                          <span class="preview-hero__dot">·</span>
                          <span>{{ msg.data.parsed.current.name }}</span>
                        </template>
                        <template v-if="msg.data.parsed.current?.schoolName">
                          <span class="preview-hero__dot">·</span>
                          <span>{{ msg.data.parsed.current.schoolName }}</span>
                        </template>
                        <template v-if="msg.data.parsed.current?.grade">
                          <span class="preview-hero__dot">·</span>
                          <span
                            >{{ msg.data.parsed.current.grade }}학년
                            {{ msg.data.parsed.current.classNum }}반</span
                          >
                        </template>
                      </div>
                    </div>

                    <div class="update-list">
                      <div class="update-list__head">
                        <v-icon icon="mdi-pencil" size="14" />
                        변경 항목 ({{
                          (msg.data.parsed.updates || []).length
                        }}개)
                      </div>
                      <ul class="update-list__items">
                        <li
                          v-for="(u, ui) in msg.data.parsed.updates || []"
                          :key="ui"
                          class="update-row"
                        >
                          <span class="update-row__field">{{
                            userFieldLabel(u.field)
                          }}</span>
                          <span class="update-row__old">{{
                            updateOldDisplay(msg.data.parsed, u.field)
                          }}</span>
                          <v-icon
                            icon="mdi-arrow-right"
                            size="14"
                            class="update-row__arrow"
                          />
                          <span class="update-row__new">{{
                            updateNewDisplay(u)
                          }}</span>
                        </li>
                      </ul>
                    </div>

                    <div
                      v-if="
                        msg.data.valid && idx === latestPreviewIdx && !executed
                      "
                      class="preview-actions"
                    >
                      <button
                        class="btn-create"
                        @click="executeCreate(msg.data)"
                        :disabled="executeLoading"
                      >
                        <v-icon icon="mdi-check" size="16" />
                        {{ executeLoading ? "변경 중..." : "변경 적용" }}
                      </button>
                      <button class="btn-cancel" @click="cancelPreview">
                        취소
                      </button>
                    </div>
                  </template>

                  <!-- ─── resetPassword intent — 비밀번호 초기화 미리보기 ─── -->
                  <template
                    v-else-if="
                      msg.data.intent === 'resetPassword' && msg.data.parsed
                    "
                  >
                    <div class="preview-hero hero-ok">
                      <div class="preview-hero__head">
                        <span class="preview-hero__badge">
                          <v-icon icon="mdi-lock-reset" size="16" />
                          비밀번호 초기화
                        </span>
                      </div>
                      <div class="preview-hero__total">
                        <span
                          class="preview-hero__unit"
                          style="
                            font-size: var(--font-h3-size);
                            color: var(--text-primary);
                            font-weight: 700;
                          "
                        >
                          비밀번호 → 000000
                        </span>
                      </div>
                      <div class="preview-hero__breakdown">
                        <span
                          ><v-icon icon="mdi-identifier" size="13" />{{
                            msg.data.parsed.userId
                          }}</span
                        >
                        <template v-if="msg.data.parsed.current?.name">
                          <span class="preview-hero__dot">·</span>
                          <span>{{ msg.data.parsed.current.name }}</span>
                        </template>
                        <template v-if="msg.data.parsed.current?.schoolName">
                          <span class="preview-hero__dot">·</span>
                          <span>{{ msg.data.parsed.current.schoolName }}</span>
                        </template>
                        <template v-if="msg.data.parsed.current?.grade">
                          <span class="preview-hero__dot">·</span>
                          <span
                            >{{ msg.data.parsed.current.grade }}학년
                            {{ msg.data.parsed.current.classNum }}반</span
                          >
                        </template>
                      </div>
                    </div>

                    <div
                      v-if="
                        msg.data.valid && idx === latestPreviewIdx && !executed
                      "
                      class="preview-actions"
                    >
                      <button
                        class="btn-create"
                        @click="executeCreate(msg.data)"
                        :disabled="executeLoading"
                      >
                        <v-icon icon="mdi-check" size="16" />
                        {{ executeLoading ? "초기화 중..." : "초기화 적용" }}
                      </button>
                      <button class="btn-cancel" @click="cancelPreview">
                        취소
                      </button>
                    </div>
                  </template>

                  <!-- ─── updateContract intent — 학교 계약 변경 미리보기 ─── -->
                  <template
                    v-else-if="
                      msg.data.intent === 'updateContract' && msg.data.parsed
                    "
                  >
                    <div class="preview-hero hero-ok">
                      <div class="preview-hero__head">
                        <span class="preview-hero__badge">
                          <v-icon icon="mdi-calendar-edit" size="16" />
                          학교 계약 기간 변경
                        </span>
                      </div>
                      <div class="preview-hero__total">
                        <span
                          class="preview-hero__unit"
                          style="
                            font-size: var(--font-h3-size);
                            color: var(--text-primary);
                            font-weight: 700;
                          "
                        >
                          {{ msg.data.parsed.grade }}학년
                          {{ contractScopeLabel(msg.data.parsed) }} 계약 변경
                        </span>
                      </div>
                      <div class="preview-hero__breakdown">
                        <span
                          ><v-icon icon="mdi-school-outline" size="13" />{{
                            msg.data.parsed.current?.schoolName ||
                            msg.data.parsed.schoolCode
                          }}</span
                        >
                        <span class="preview-hero__dot">·</span>
                        <span class="mono">{{
                          msg.data.parsed.schoolCode
                        }}</span>
                        <span class="preview-hero__dot">·</span>
                        <span>{{ msg.data.parsed.publisher }}</span>
                      </div>
                    </div>

                    <div class="update-list">
                      <div class="update-list__head">
                        <v-icon icon="mdi-calendar-arrow-right" size="14" />
                        변경 내역 ({{
                          (msg.data.parsed.current?.contracts || []).length
                        }}개 반)
                      </div>
                      <ul class="update-list__items">
                        <li
                          v-for="(c, ci) in msg.data.parsed.current
                            ?.contracts || []"
                          :key="ci"
                          class="contract-row"
                        >
                          <span class="contract-row__class"
                            >{{ c.classNumber }}반</span
                          >
                          <div class="contract-row__dates">
                            <div class="contract-row__before">
                              <span class="contract-row__label">현재</span>
                              <span class="tabular-nums mono"
                                >{{ c.startDate || "-" }} ~
                                {{ c.endDate || "-" }}</span
                              >
                            </div>
                            <div class="contract-row__after">
                              <span class="contract-row__label">변경 후</span>
                              <span class="tabular-nums mono"
                                >{{ msg.data.parsed.startDate }} ~
                                {{ msg.data.parsed.endDate }}</span
                              >
                            </div>
                          </div>
                        </li>
                      </ul>
                    </div>

                    <div
                      v-if="
                        msg.data.valid && idx === latestPreviewIdx && !executed
                      "
                      class="preview-actions"
                    >
                      <button
                        class="btn-create"
                        @click="executeCreate(msg.data)"
                        :disabled="executeLoading"
                      >
                        <v-icon icon="mdi-check" size="16" />
                        {{ executeLoading ? "변경 중..." : "변경 적용" }}
                      </button>
                      <button class="btn-cancel" @click="cancelPreview">
                        취소
                      </button>
                    </div>
                  </template>

                  <!-- ─── updateLmsClasses intent — LMS 담당학급 변경 미리보기 ─── -->
                  <template
                    v-else-if="
                      msg.data.intent === 'updateLmsClasses' && msg.data.parsed
                    "
                  >
                    <div class="preview-hero hero-ok">
                      <div class="preview-hero__head">
                        <span class="preview-hero__badge">
                          <v-icon
                            :icon="
                              msg.data.parsed.mode === 'add'
                                ? 'mdi-playlist-plus'
                                : 'mdi-account-switch'
                            "
                            size="16"
                          />
                          LMS 담당 학급
                          {{
                            msg.data.parsed.mode === "add" ? "추가" : "교체"
                          }}
                        </span>
                      </div>
                      <div class="preview-hero__total">
                        <span
                          class="preview-hero__unit"
                          style="
                            font-size: var(--font-h3-size);
                            color: var(--text-primary);
                            font-weight: 700;
                          "
                        >
                          {{ msg.data.parsed.assignments.length }}개 학년 /
                          {{ lmsTotalClasses(msg.data.parsed.assignments) }}개 반
                        </span>
                      </div>
                      <div class="preview-hero__breakdown">
                        <span
                          ><v-icon icon="mdi-identifier" size="13" />{{
                            msg.data.parsed.userId
                          }}</span
                        >
                        <template v-if="msg.data.parsed.current?.name">
                          <span class="preview-hero__dot">·</span>
                          <span>{{ msg.data.parsed.current.name }}</span>
                        </template>
                        <template v-if="msg.data.parsed.current?.schoolName">
                          <span class="preview-hero__dot">·</span>
                          <span>{{ msg.data.parsed.current.schoolName }}</span>
                        </template>
                      </div>
                    </div>

                    <div class="update-list">
                      <div class="update-list__head">
                        <v-icon icon="mdi-account-multiple-outline" size="14" />
                        {{
                          msg.data.parsed.mode === "add"
                            ? "추가할 담당 학급"
                            : "새 담당 학급 (전체 교체)"
                        }}
                      </div>
                      <ul class="update-list__items">
                        <li
                          v-for="(a, ai) in msg.data.parsed.assignments"
                          :key="ai"
                          class="update-list__item"
                        >
                          <strong>{{ a.grade }}학년</strong>
                          {{ a.classNumbers.join(", ") }}반
                          <span class="text-tertiary">· {{ a.publisher }}</span>
                        </li>
                      </ul>
                    </div>

                    <div
                      v-if="
                        msg.data.parsed.current &&
                        Array.isArray(msg.data.parsed.current.classList)
                      "
                      class="update-list update-list--before"
                    >
                      <div class="update-list__head">
                        <v-icon icon="mdi-history" size="14" />
                        기존 담당
                      </div>
                      <ul class="update-list__items">
                        <li
                          v-for="(slot, gi) in msg.data.parsed.current.classList"
                          :key="gi"
                          v-show="slot && slot.length > 0"
                          class="update-list__item"
                        >
                          <strong>{{ gi + 1 }}학년</strong>
                          {{ slot.join(", ") }}반
                          <span
                            v-if="
                              msg.data.parsed.current.publisherList &&
                              msg.data.parsed.current.publisherList[gi] &&
                              msg.data.parsed.current.publisherList[gi].length
                            "
                            class="text-tertiary"
                          >
                            · {{ msg.data.parsed.current.publisherList[gi][0] }}
                          </span>
                        </li>
                        <li
                          v-if="
                            (msg.data.parsed.current.classList || []).every(
                              (s) => !s || s.length === 0,
                            )
                          "
                          class="update-list__item text-tertiary"
                        >
                          (기존 담당 없음)
                        </li>
                      </ul>
                    </div>

                    <div
                      v-if="
                        msg.data.valid && idx === latestPreviewIdx && !executed
                      "
                      class="preview-actions"
                    >
                      <button
                        class="btn-create"
                        @click="executeCreate(msg.data)"
                        :disabled="executeLoading"
                      >
                        <v-icon icon="mdi-check" size="16" />
                        {{ executeLoading ? "변경 중..." : "변경 적용" }}
                      </button>
                      <button class="btn-cancel" @click="cancelPreview">
                        취소
                      </button>
                    </div>
                  </template>

                  <!-- ─── createBatchUsers intent (기본) — 계정 발급 미리보기 ─── -->
                  <template v-else>
                    <!-- Hero 영역 -->
                    <div
                      class="preview-hero"
                      :class="msg.data.valid ? 'hero-ok' : 'hero-err'"
                    >
                      <div class="preview-hero__head">
                        <span class="preview-hero__badge">
                          <v-icon
                            :icon="
                              msg.data.valid
                                ? 'mdi-check-circle'
                                : 'mdi-alert-circle'
                            "
                            size="16"
                          />
                          {{ msg.data.valid ? "생성 가능" : "검증 실패" }}
                        </span>
                      </div>
                      <template v-if="msg.data.valid && msg.data.parsed">
                        <div class="preview-hero__total">
                          <span class="preview-hero__num">{{
                            computeTotal(msg.data.parsed)
                          }}</span>
                          <span class="preview-hero__unit">개 계정 생성</span>
                        </div>
                        <div class="preview-hero__breakdown">
                          <template v-if="teacherCount(msg.data.parsed) > 0">
                            <span>
                              <v-icon icon="mdi-account-tie" size="13" />
                              교사 {{ teacherCount(msg.data.parsed)
                              }}<template
                                v-if="
                                  Array.isArray(msg.data.parsed.teacherClasses)
                                "
                              >
                                ({{
                                  msg.data.parsed.teacherClasses.join(",")
                                }}반)</template
                              >
                            </span>
                            <span class="preview-hero__dot">·</span>
                          </template>
                          <span
                            ><v-icon
                              icon="mdi-account-multiple"
                              size="13"
                            />학생 {{ sumStudents(msg.data.parsed) }}</span
                          >
                          <template v-if="lmsCount(msg.data.parsed) > 0">
                            <span class="preview-hero__dot">·</span>
                            <span>
                              <v-icon icon="mdi-school" size="13" />
                              LMS {{ lmsCount(msg.data.parsed) }} ({{
                                msg.data.parsed.lmsClasses.join(",")
                              }}반)
                            </span>
                          </template>
                        </div>
                      </template>
                      <template v-else>
                        <p class="preview-hero__summary">
                          {{ msg.data.summary }}
                        </p>
                      </template>
                    </div>

                    <!-- 파싱 정보 -->
                    <template v-if="msg.data.parsed && msg.data.parsed.classes">
                      <div class="preview-info-grid">
                        <div class="preview-info-cell">
                          <span class="preview-info-cell__label">
                            <v-icon icon="mdi-school-outline" size="13" /> 학교
                          </span>
                          <span class="preview-info-cell__value">{{
                            msg.data.parsed.schoolName
                          }}</span>
                        </div>
                        <div class="preview-info-cell">
                          <span class="preview-info-cell__label">
                            <v-icon icon="mdi-pound" size="13" /> 코드
                          </span>
                          <span class="preview-info-cell__value mono">{{
                            msg.data.parsed.schoolCode
                          }}</span>
                        </div>
                        <div class="preview-info-cell">
                          <span class="preview-info-cell__label">
                            <v-icon icon="mdi-numeric" size="13" /> 학년
                          </span>
                          <span class="preview-info-cell__value"
                            >{{ msg.data.parsed.grade }}학년</span
                          >
                        </div>
                        <div class="preview-info-cell">
                          <span class="preview-info-cell__label">
                            <v-icon icon="mdi-book-open-variant" size="13" />
                            출판사
                          </span>
                          <span class="preview-info-cell__value">{{
                            msg.data.parsed.publisher
                          }}</span>
                        </div>
                        <div
                          v-if="
                            msg.data.parsed.startDate && msg.data.parsed.endDate
                          "
                          class="preview-info-cell"
                          style="grid-column: 1 / -1"
                        >
                          <span class="preview-info-cell__label">
                            <v-icon icon="mdi-calendar-range" size="13" />
                            학교 계약 기간
                          </span>
                          <span class="preview-info-cell__value">
                            {{ msg.data.parsed.startDate }} ~
                            {{ msg.data.parsed.endDate }}
                          </span>
                        </div>
                      </div>

                      <!-- 학급별 -->
                      <div class="preview-classes">
                        <div class="preview-classes__head">
                          <v-icon icon="mdi-format-list-bulleted" size="14" />
                          학급 구성 ({{ msg.data.parsed.classes.length }}개 반)
                        </div>
                        <ul class="preview-classes__list">
                          <li
                            v-for="(cls, ci) in msg.data.parsed.classes"
                            :key="ci"
                            class="preview-class-row"
                          >
                            <span class="preview-class-row__num"
                              >{{ cls.classNum }}반</span
                            >
                            <span class="preview-class-row__count">
                              <v-icon
                                icon="mdi-account-multiple-outline"
                                size="13"
                              />
                              <template
                                v-if="
                                  Array.isArray(cls.attendanceNumbers) &&
                                  cls.attendanceNumbers.length > 0
                                "
                              >
                                {{ cls.attendanceNumbers.join(",") }}번
                                ({{ cls.attendanceNumbers.length }}명)
                              </template>
                              <template v-else>
                                학생 {{ cls.studentCount }}명
                              </template>
                            </span>
                            <span class="preview-class-row__teacher">
                              <v-icon
                                icon="mdi-account-tie-outline"
                                size="13"
                              />
                              {{ cls.teacherName || "담임 자동생성" }}
                            </span>
                          </li>
                        </ul>
                      </div>
                    </template>

                    <!-- 경고 -->
                    <div
                      v-if="msg.data.warnings && msg.data.warnings.length"
                      class="preview-warn"
                    >
                      <p><v-icon icon="mdi-alert-outline" size="14" /> 안내</p>
                      <ul>
                        <li v-for="(w, wi) in msg.data.warnings" :key="wi">
                          {{ w }}
                        </li>
                      </ul>
                    </div>

                    <!-- 오류 -->
                    <div
                      v-if="msg.data.errors && msg.data.errors.length"
                      class="preview-error"
                    >
                      <p>
                        <v-icon icon="mdi-close-circle-outline" size="14" />
                        오류
                      </p>
                      <ul>
                        <li v-for="(e, ei) in msg.data.errors" :key="ei">
                          {{ e }}
                        </li>
                      </ul>
                    </div>

                    <!-- 생성 버튼 — 가장 최신 preview이고 아직 실행 전일 때만 표시 -->
                    <div
                      v-if="
                        msg.data.valid && idx === latestPreviewIdx && !executed
                      "
                      class="preview-actions"
                    >
                      <button
                        class="btn-create"
                        @click="executeCreate(msg.data)"
                        :disabled="executeLoading"
                      >
                        <v-icon icon="mdi-check" size="16" />
                        {{ executeLoading ? "생성 중..." : "이대로 생성" }}
                      </button>
                      <button class="btn-cancel" @click="cancelPreview">
                        취소
                      </button>
                    </div>
                  </template>
                  <!-- /createBatchUsers 블록 끝 -->
                </div>
              </template>

              <!-- 결과 카드 -->
              <template v-else-if="msg.type === 'result'">
                <div class="result-card">
                  <div class="result-title">🎉 생성 완료</div>
                  <p class="result-summary">
                    총 <b>{{ msg.data.totalCreated }}개</b> 계정 생성
                    &nbsp;·&nbsp; {{ msg.data.schoolCode }} &nbsp;·&nbsp;
                    {{ msg.data.grade }}학년 &nbsp;·&nbsp;
                    {{ msg.data.publisher }}
                  </p>

                  <details class="result-details">
                    <summary>
                      계정 ID 목록 ({{ msg.data.accounts.length }}개) 보기
                    </summary>
                    <div class="result-table-wrap">
                      <table class="preview-table" style="margin-top: 8px">
                        <thead>
                          <tr>
                            <th>역할</th>
                            <th>ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(acc, ai) in msg.data.accounts" :key="ai">
                            <td>
                              {{
                                acc.role === "teacher"
                                  ? "교사"
                                  : acc.role === "lms"
                                  ? "LMS"
                                  : "학생"
                              }}
                            </td>
                            <td class="mono">{{ acc.id }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </details>

                  <button class="btn-csv" @click="downloadCsv(msg.data)">
                    <v-icon icon="mdi-download" size="14" />
                    CSV 다운로드
                  </button>
                </div>
              </template>
            </div>
          </div>

          <!-- 타이핑 인디케이터 -->
          <div v-if="isThinking" class="nlp-row ai">
            <div class="nlp-avatar">
              <v-icon icon="mdi-robot-outline" color="white" size="15" />
            </div>
            <div class="nlp-bubble ai">
              <div class="typing"><span></span><span></span><span></span></div>
            </div>
          </div>
        </div>

        <!-- ── 입력창 ── -->
        <div class="nlp-input-wrap">
          <textarea
            ref="inputRef"
            v-model="inputText"
            @keydown.enter.exact.prevent="sendMessage"
            placeholder="예: 흥국 초등학교 5학년 1,2,3반 각 25명씩 미래엔으로 만들어줘  (Enter 전송 / Shift+Enter 줄바꿈)"
            rows="2"
            spellcheck="false"
            class="nlp-input"
            :disabled="isThinking || executeLoading"
          />
          <button
            class="nlp-send-btn"
            @click="sendMessage"
            :disabled="!inputText.trim() || isThinking || executeLoading"
          >
            <v-icon icon="mdi-send" color="white" size="18" />
          </button>
        </div>
      </div>
</template>

<script>
import { nlpPreview, nlpExecute } from "@/api/user-api-list";

export default {
  // DefaultLayout의 <keep-alive include="NlpAccountCreate">가 이 이름으로 매칭해
  // 탭(라우트) 이동 시에도 인스턴스를 파괴하지 않는다 — 대화/입력 상태 유지.
  name: "NlpAccountCreate",
  data() {
    return {
      messages: [],
      inputText: "",
      isThinking: false,
      executeLoading: false,
      latestPreviewIdx: -1, // 마지막 preview 메시지 인덱스
      executed: false, // 이미 생성 실행했는지 여부
      pendingPartial: null, // multi-turn 누적 입력 — 검증 통과 시 클리어
      pendingConfirm: null, // 검증 통과 후 최종 생성/취소 확인 대기 — parsed 객체
    };
  },

  mounted() {
    this.addAI(
      "text",
      "무엇을 도와드릴까요?\n\n" +
        "다음 작업을 요청하실 수 있어요:\n" +
        '• 계정 발급 (예: "흥국초 5학년 1,2반 각 25명씩 미래엔으로 만들어줘")\n' +
        '• 유저 정보 수정 (예: "aqz260301 학생 이름을 김철수로 바꿔줘")\n' +
        '• 학교 계약 기간 변경 (예: "aqz 학교 5학년 계약기간을 2026-03-01 ~ 2027-02-28로 변경")\n' +
        '• 비밀번호 초기화 (예: "aqz260301 PW 초기화" 또는 "한강초 김철수 PW 초기화")\n' +
        '• LMS 담당학급 변경 (예: "lmsaqz2601 담당 학급을 3학년 1,2반으로")',
    );
  },

  methods: {
    /* ─── AI 텍스트 렌더 — 허용된 안전 HTML만 통과 ─── */
    renderAiText(raw) {
      if (!raw) return "";
      // 1단계: 모든 < > 를 escape
      let s = String(raw)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      // 2단계: 화이트리스트 태그/속성만 복원
      // <table class="publisher-table">, <thead>, <tbody>, <tr>, <th>, <td class="pub-code">
      s = s
        .replace(
          /&lt;table class="publisher-table"&gt;/g,
          '<table class="publisher-table">',
        )
        .replace(/&lt;\/table&gt;/g, "</table>")
        .replace(/&lt;thead&gt;/g, "<thead>")
        .replace(/&lt;\/thead&gt;/g, "</thead>")
        .replace(/&lt;tbody&gt;/g, "<tbody>")
        .replace(/&lt;\/tbody&gt;/g, "</tbody>")
        .replace(/&lt;tr&gt;/g, "<tr>")
        .replace(/&lt;\/tr&gt;/g, "</tr>")
        .replace(/&lt;th&gt;/g, "<th>")
        .replace(/&lt;\/th&gt;/g, "</th>")
        .replace(/&lt;td&gt;/g, "<td>")
        .replace(/&lt;td class="pub-code"&gt;/g, '<td class="pub-code">')
        .replace(/&lt;\/td&gt;/g, "</td>");
      return s;
    },

    /* ─── updateUser 표시 헬퍼 (multi-field) ─── */
    userFieldLabel(field) {
      switch (field) {
        case "name":
          return "이름";
        case "password":
          return "비밀번호";
        case "grade":
          return "학년";
        case "classNum":
          return "반";
        case "publisher":
          return "출판사";
        default:
          return field || "정보";
      }
    },
    /** updates 배열 기반 요약 라벨 — "이름, 출판사 변경" 식 */
    updateSummaryLabel(parsed) {
      const updates = parsed?.updates || [];
      if (updates.length === 0) return "정보 변경";
      const fields = updates.map((u) => this.userFieldLabel(u.field));
      return `${fields.join(", ")} 변경`;
    },
    /** 특정 field의 변경 전 값 표시 */
    updateOldDisplay(parsed, field) {
      const cur = parsed?.current ?? {};
      if (field === "password") return "(기존 비밀번호)";
      if (field === "name") return cur.name || "-";
      if (field === "grade")
        return cur.grade != null ? `${cur.grade}학년` : "-";
      if (field === "classNum")
        return cur.classNum != null ? `${cur.classNum}반` : "-";
      if (field === "publisher") return cur.publisher || "-";
      return "-";
    },
    /** 계약 변경 범위 라벨 — "전체 반" 또는 "1,2반" */
    contractScopeLabel(parsed) {
      const cn = parsed?.classNumbers;
      if (!Array.isArray(cn) || cn.length === 0) return "전체 반";
      // 학년에 등록된 반 수와 같으면 "전체" 처리
      const total = (parsed?.current?.contracts || []).length;
      if (cn.length === total) return "전체 반";
      return `${cn.join(",")}반`;
    },

    /** 단일 update 항목의 새 값 표시 */
    updateNewDisplay(u) {
      if (!u) return "-";
      if (u.field === "password") return "(새 비밀번호)";
      if (u.field === "grade") return `${u.newValue}학년`;
      if (u.field === "classNum") return `${u.newValue}반`;
      return u.newValue ?? "-";
    },

    /* ─── 카운트 헬퍼 ─── */
    sumStudents(parsed) {
      return (parsed?.classes ?? []).reduce((s, c) => {
        // attendanceNumbers 지정 시 그 길이가 학생 수, 아니면 studentCount
        if (Array.isArray(c.attendanceNumbers) && c.attendanceNumbers.length > 0) {
          return s + c.attendanceNumbers.length;
        }
        return s + (c.studentCount || 0);
      }, 0);
    },
    lmsCount(parsed) {
      return Array.isArray(parsed?.lmsClasses) ? parsed.lmsClasses.length : 0;
    },
    teacherCount(parsed) {
      // teacherClasses 생략(undefined) → 모든 반에 교사 (역호환). 배열이면 그 길이만큼.
      if (parsed?.teacherClasses === undefined) {
        return (parsed?.classes ?? []).length;
      }
      return Array.isArray(parsed.teacherClasses)
        ? parsed.teacherClasses.length
        : 0;
    },
    computeTotal(parsed) {
      const students = this.sumStudents(parsed);
      return students + this.teacherCount(parsed) + this.lmsCount(parsed);
    },
    /** updateLmsClasses: assignments 전체 반 수 합산 */
    lmsTotalClasses(assignments) {
      if (!Array.isArray(assignments)) return 0;
      return assignments.reduce(
        (sum, a) => sum + (Array.isArray(a.classNumbers) ? a.classNumbers.length : 0),
        0,
      );
    },

    /* ─── 메시지 추가 헬퍼 ─── */
    addAI(type, textOrData) {
      if (type === "text") {
        this.messages.push({ role: "ai", type: "text", text: textOrData });
      } else if (type === "preview") {
        this.latestPreviewIdx = this.messages.length;
        this.messages.push({ role: "ai", type: "preview", data: textOrData });
      } else if (type === "result") {
        this.messages.push({ role: "ai", type: "result", data: textOrData });
      }
      this.scrollDown();
    },

    addUser(text) {
      this.messages.push({ role: "user", type: "text", text });
      this.scrollDown();
    },

    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.chatBody;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    /* ─── 입력창 포커스 복원 ─── */
    refocusInput() {
      this.$nextTick(() => {
        const el = this.$refs.inputRef;
        if (el && typeof el.focus === "function" && !el.disabled) {
          el.focus();
        }
      });
    },

    /* ─── 메시지 전송 ─── */
    async sendMessage() {
      const text = this.inputText.trim();
      if (!text || this.isThinking || this.executeLoading) return;

      this.addUser(text);
      this.inputText = "";

      // 최종 생성/취소 확인 대기 중인 경우
      if (this.pendingConfirm) {
        const yes =
          /^(네|예|yes|y|좋|생성|발급|변경|적용|만들|진행|ok|okay)/i.test(text);
        const no = /^(아니|no|n|취소|중단|싫)/i.test(text);
        if (yes) {
          const payload = this.pendingConfirm;
          this.pendingConfirm = null;
          await this.executeCreate(payload);
          this.refocusInput();
          return;
        }
        if (no) {
          this.pendingConfirm = null;
          this.pendingPartial = null;
          this.addAI(
            "text",
            "발급을 취소했어요. 새 명령을 입력하시면 다시 진행할 수 있어요.",
          );
          this.refocusInput();
          return;
        }
        // 모호한 응답 → 다시 묻기
        this.addAI(
          "text",
          '"네" 또는 "아니오"로 답해주세요. 또는 위 카드의 [이대로 생성] 버튼을 눌러도 돼요.',
        );
        this.refocusInput();
        return;
      }

      this.executed = false;
      this.isThinking = true;

      try {
        const res = await nlpPreview(text, this.pendingPartial);
        if (!res || !res.data) {
          this.addAI(
            "text",
            "서버 응답을 받지 못했습니다. 로그인 상태를 확인해주세요.",
          );
          return;
        }
        const payload = res.data.data ?? res.data;

        if (!payload.valid) {
          // 검증 실패 — preview 카드는 띄우지 않고, AI가 누락 정보만 자연어로 되묻기
          this.pendingPartial = payload.partial || null;
          const followUp =
            payload.followUp ||
            (payload.errors && payload.errors[0]) ||
            "입력을 다시 확인해주세요.";
          this.addAI("text", followUp);
        } else {
          // 검증 통과 — partial 클리어, 미리보기 카드 + 생성/취소 확인 요청
          this.pendingPartial = null;
          this.addAI("preview", payload);
          const intent = payload.intent || "createBatchUsers";
          if (intent === "updateUser") {
            this.addAI(
              "text",
              `위 내용대로 변경할까요? "네" 또는 "아니오"로 답해주세요.\n` +
                `(또는 위 카드의 [변경 적용] 버튼을 눌러도 됩니다)`,
            );
          } else if (intent === "updateContract") {
            const c = payload.parsed || {};
            const targets =
              Array.isArray(c.classNumbers) && c.classNumbers.length > 0
                ? `${c.classNumbers.join(",")}반`
                : "전체 반";
            const n = (c.current?.contracts || []).length;
            this.addAI(
              "text",
              `${c.grade}학년 ${targets} (${n}개 반)의 계약 기간을 다음과 같이 변경할 준비가 됐어요.\n` +
                `  ${c.startDate} ~ ${c.endDate}\n` +
                `위 내용대로 적용할까요? "네" 또는 "아니오"로 답해주세요.\n` +
                `(또는 위 카드의 [변경 적용] 버튼을 눌러도 됩니다)`,
            );
          } else if (intent === "resetPassword") {
            const p = payload.parsed || {};
            const name = p.current?.name || "";
            this.addAI(
              "text",
              `${p.userId}${
                name ? ` (${name})` : ""
              }의 비밀번호를 000000으로 초기화할 준비가 됐어요.\n` +
                `진행할까요? "네" 또는 "아니오"로 답해주세요.\n` +
                `(또는 위 카드의 [초기화 적용] 버튼을 눌러도 됩니다)`,
            );
          } else if (intent === "updateLmsClasses") {
            const p = payload.parsed || {};
            const total = this.lmsTotalClasses(p.assignments || []);
            const summary = (p.assignments || [])
              .map(
                (a) =>
                  `${a.grade}학년 ${a.classNumbers.join(",")}반 (${a.publisher})`,
              )
              .join(" · ");
            const modeLabel =
              p.mode === "add" ? "추가" : "교체 (기존 담당 모두 초기화)";
            this.addAI(
              "text",
              `${p.userId}${p.current?.name ? ` (${p.current.name})` : ""}의 담당 학급을 ${modeLabel} 모드로 적용할 준비가 됐어요.\n` +
                `  ${summary}\n  총 ${p.assignments.length}개 학년 / ${total}개 반\n` +
                `위 내용대로 적용할까요? "네" 또는 "아니오"로 답해주세요.\n` +
                `(또는 위 카드의 [변경 적용] 버튼을 눌러도 됩니다)`,
            );
          } else {
            const total = this.computeTotal(payload.parsed);
            this.addAI(
              "text",
              `총 ${total}개 계정을 발급할 준비가 됐어요.\n` +
                `위 내용대로 생성할까요? "네" 또는 "아니오"로 답해주세요.\n` +
                `(또는 위 카드의 [이대로 생성] 버튼을 눌러도 됩니다)`,
            );
          }
          // pendingConfirm은 payload 전체(intent + parsed)를 보관해서 executeCreate가 다시 처리
          this.pendingConfirm = payload;
        }
      } catch (e) {
        this.addAI("text", `오류가 발생했습니다: ${e.message ?? e}`);
      } finally {
        this.isThinking = false;
        this.refocusInput();
      }
    },

    /* ─── 계정 생성 실행 ─── */
    async executeCreate(previewData) {
      if (!previewData?.parsed) return;
      this.executeLoading = true;
      this.executed = true; // 버튼 즉시 숨김
      this.pendingConfirm = null; // 텍스트 확인 흐름과 동기화

      const intent = previewData.intent || "createBatchUsers";
      const parsed = previewData.parsed;

      // intent 별 사용자 액션 메시지
      if (intent === "createBatchUsers") {
        const total = this.computeTotal(parsed);
        this.addUser(`${total}개 계정을 생성합니다.`);
      } else if (intent === "updateUser") {
        this.addUser(
          `${parsed.userId} ${this.updateSummaryLabel(parsed)} 적용`,
        );
      } else if (intent === "updateContract") {
        this.addUser(
          `${parsed.schoolCode} ${parsed.grade}학년 계약 기간 변경 적용`,
        );
      } else if (intent === "resetPassword") {
        this.addUser(`${parsed.userId} 비밀번호 초기화 적용`);
      } else if (intent === "updateLmsClasses") {
        const label = parsed.mode === "add" ? "추가" : "교체";
        this.addUser(`${parsed.userId} LMS 담당학급 ${label} 적용`);
      } else {
        this.addUser("요청을 실행합니다.");
      }
      this.isThinking = true;

      try {
        // intent 라우팅 형태로 호출
        const body = { intent, payload: parsed };
        const res = await nlpExecute(body);
        if (!res || !res.data) {
          this.addAI("text", "서버 응답을 받지 못했습니다.");
          return;
        }
        const result = res.data.data ?? res.data;

        if (intent === "createBatchUsers") {
          this.addAI("result", result);
          const contractLine =
            result?.contract?.registered &&
            result.contract.startDate &&
            result.contract.endDate
              ? `✅ 학교 계약 등록: ${result.contract.startDate} ~ ${result.contract.endDate}\n`
              : "";
          this.addAI(
            "text",
            contractLine +
              "계정 발급이 완료되었습니다! 새로운 명령을 입력하시면 추가 발급도 가능합니다.",
          );
        } else if (intent === "updateUser") {
          if (result?.error) {
            this.addAI("text", `변경 실패: ${result.error}`);
          } else {
            const formatVal = (field, val) => {
              if (field === "password") return "(비밀번호)";
              if (field === "grade") return val != null ? `${val}학년` : "-";
              if (field === "classNum") return val != null ? `${val}반` : "-";
              return val != null ? `"${val}"` : "-";
            };
            const lines = (result.results || []).map((r) => {
              const label = this.userFieldLabel(r.field);
              const mark = r.success ? "✓" : "✗";
              return `  ${mark} ${label}: ${formatVal(
                r.field,
                r.oldValue,
              )} → ${formatVal(r.field, r.newValue)}`;
            });
            const status = result.success
              ? "✅ 변경 완료"
              : "⚠️ 일부 변경 실패";
            this.addAI(
              "text",
              `${status} (${result.userId})\n${lines.join("\n")}`,
            );
          }
        } else if (intent === "updateContract") {
          if (result?.error || result?.success === false) {
            this.addAI(
              "text",
              `변경 실패: ${result?.error || "알 수 없는 오류"}`,
            );
          } else {
            const targets =
              Array.isArray(result.classNumbers) &&
              result.classNumbers.length > 0
                ? `${result.classNumbers.join(",")}반`
                : "전체 반";
            this.addAI(
              "text",
              `✅ 계약 기간 변경 완료\n` +
                `  학교: ${result.schoolName || result.schoolCode}\n` +
                `  학년/범위: ${result.grade}학년 ${targets} (${result.changedCount}개 반 적용)\n` +
                `  기간: ${result.newStartDate} ~ ${result.newEndDate}\n` +
                `  출판사: ${result.newPublisher}`,
            );
          }
        } else if (intent === "resetPassword") {
          if (result?.error || result?.success === false) {
            this.addAI(
              "text",
              `초기화 실패: ${result?.error || "알 수 없는 오류"}`,
            );
          } else {
            const u = result.user || {};
            this.addAI(
              "text",
              `✅ 비밀번호 초기화 완료\n` +
                `  ID: ${result.userId}${u.name ? ` (${u.name})` : ""}\n` +
                `  ${
                  u.schoolName ? `학교: ${u.schoolName}\n  ` : ""
                }새 비밀번호: ${result.newPassword}`,
            );
          }
        } else if (intent === "updateLmsClasses") {
          if (result?.error || result?.success === false) {
            this.addAI(
              "text",
              `변경 실패: ${result?.error || "알 수 없는 오류"}`,
            );
          } else {
            const u = result.user || {};
            const label = result.mode === "add" ? "추가" : "교체";
            const finalLines = (result.classList || [])
              .map((slot, gi) => {
                if (!slot || slot.length === 0) return null;
                const pubs = (result.publisherList || [])[gi] || [];
                const pub = pubs[0] || "";
                return `  · ${gi + 1}학년 ${slot.join(",")}반${pub ? ` (${pub})` : ""}`;
              })
              .filter(Boolean);
            this.addAI(
              "text",
              `✅ LMS 담당학급 ${label} 완료\n` +
                `  ID: ${result.userId}${u.name ? ` (${u.name})` : ""}\n` +
                (u.schoolName ? `  학교: ${u.schoolName}\n` : "") +
                `  현재 담당 (전체):\n` +
                (finalLines.length ? finalLines.join("\n") : "  · (없음)"),
            );
          }
        }
      } catch (e) {
        this.addAI("text", `실행 중 오류: ${e.message ?? e}`);
      } finally {
        this.isThinking = false;
        this.executeLoading = false;
        this.refocusInput();
      }
    },

    /* ─── 취소 ─── */
    cancelPreview() {
      this.executed = true;
      this.pendingConfirm = null;
      this.pendingPartial = null;
      this.addAI("text", "취소했습니다. 새 명령을 입력해주세요.");
    },

    /* ─── 초기화 ─── */
    resetChat() {
      this.messages = [];
      this.inputText = "";
      this.isThinking = false;
      this.executeLoading = false;
      this.latestPreviewIdx = -1;
      this.executed = false;
      this.pendingPartial = null;
      this.pendingConfirm = null;
      this.addAI("text", "초기화되었습니다.\n\n어떤 학교의 계정을 만들까요?");
    },

    /* ─── CSV 다운로드 ─── */
    downloadCsv(data) {
      if (!data?.accounts) return;

      // CSV 셀 이스케이프: 쌍따옴표/콤마/개행 포함 시 double-quote 처리
      const esc = (v) => {
        const s = String(v ?? "");
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      // Excel 등이 숫자로 자동 변환하지 않도록 "텍스트 강제 수식" 포맷 (="000000")
      const asText = (v) => `="${String(v ?? "").replace(/"/g, '""')}"`;

      const roleLabel = (r) => {
        if (r === "teacher") return "교사";
        if (r === "lms") return "LMS";
        return "학생";
      };
      const rows = [
        ["역할", "ID", "초기비밀번호"],
        ...data.accounts.map((a) => [
          esc(roleLabel(a.role)),
          asText(a.id), // ID도 leading-zero 보존
          asText("000000"), // 비밀번호는 무조건 텍스트로
        ]),
      ];
      const csv = rows.map((r) => r.join(",")).join("\r\n");
      // UTF-8 BOM 으로 한글/Excel 호환
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `accounts_${data.schoolCode}_${data.grade}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
/* ── 전체 레이아웃 ── */
.nlp-page {
  display: flex;
  flex-direction: column;
  height: clamp(360px, calc(100dvh - 320px), 640px);
  padding: 0;
}

@media (max-width: 640px) {
  .nlp-page {
    height: clamp(320px, calc(100dvh - 280px), 560px);
  }
}

/* ── 채팅 본문 ── */
.nlp-body {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-5) var(--space-4);
  background: var(--bg-canvas);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

/* ── 행 (메시지 단위) ── */
.nlp-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.nlp-row.user {
  flex-direction: row-reverse;
}

/* ── AI 아바타 ── */
.nlp-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--color-primary-900);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-bottom: 2px;
}

/* ── 버블 ── */
.nlp-bubble {
  max-width: 78%;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;
}
.nlp-bubble.ai {
  background: var(--bg-surface-raised);
  color: var(--text-primary);
  border: 1px solid var(--border-default);
  border-bottom-left-radius: 4px;
  box-shadow: var(--shadow-sm);
}
.nlp-bubble.user {
  background: var(--color-primary-600);
  color: var(--text-on-primary);
  border-bottom-right-radius: 4px;
  box-shadow: var(--shadow-sm);
}

.nlp-text {
  white-space: pre-line;
}

/* 출판사 표 — 좁은 채팅 버블 안에서도 안 잘리게 */
.nlp-text :deep(.publisher-table) {
  display: table;
  width: 100%;
  max-width: 100%;
  margin: var(--space-2) 0 0;
  border-collapse: collapse;
  background: var(--bg-surface-raised);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  overflow: hidden;
  font-size: var(--font-caption-size);
  white-space: normal;
  table-layout: fixed;
}
.nlp-text :deep(.publisher-table thead) {
  background: var(--bg-subtle);
}
.nlp-text :deep(.publisher-table th) {
  padding: 6px 8px;
  text-align: left;
  font-weight: 600;
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border-default);
}
.nlp-text :deep(.publisher-table td) {
  padding: 5px 8px;
  border-bottom: 1px solid var(--border-default);
  color: var(--text-primary);
  word-break: keep-all;
  overflow-wrap: anywhere;
}
.nlp-text :deep(.publisher-table tbody tr:last-child td) {
  border-bottom: none;
}
.nlp-text :deep(.publisher-table th:first-child),
.nlp-text :deep(.publisher-table td.pub-code) {
  width: 38%;
}
.nlp-text :deep(.publisher-table td.pub-code) {
  font-family: var(--font-mono-family, "JetBrains Mono", "D2Coding", monospace);
  font-weight: 600;
  color: var(--color-primary-700);
}

/* AI 버블이 표를 담을 때 폭 여유 확보 */
.nlp-bubble.ai {
  max-width: 90%;
}
@media (max-width: 640px) {
  .nlp-bubble.ai {
    max-width: 94%;
  }
}

/* ── 타이핑 인디케이터 ── */
.typing {
  display: flex;
  gap: 4px;
  padding: 2px 4px;
  align-items: center;
  height: 20px;
}
.typing span {
  width: 7px;
  height: 7px;
  background: var(--text-tertiary);
  border-radius: 50%;
  animation: bounce 1.2s infinite ease-in-out;
}
.typing span:nth-child(2) {
  animation-delay: 0.2s;
}
.typing span:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes bounce {
  0%,
  60%,
  100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-6px);
  }
}

/* ── 미리보기 카드 ── */
.preview-card {
  margin: 2px 0;
  border-radius: var(--radius-lg);
  overflow: hidden;
  font-size: var(--font-small-size);
  border: 1px solid var(--border-default);
  background: var(--bg-surface-raised);
  box-shadow: var(--shadow-sm);
}
.preview-card.valid {
  border-color: var(--color-primary-300, #7fa8f4);
}
.preview-card.invalid {
  border-color: var(--color-error-text, #b42318);
}

/* Hero 영역 */
.preview-hero {
  padding: var(--space-4) var(--space-5);
  position: relative;
}
.preview-hero.hero-ok {
  background: linear-gradient(
    135deg,
    var(--color-primary-50) 0%,
    var(--bg-surface-raised) 100%
  );
}
.preview-hero.hero-err {
  background: var(--color-error-bg);
}

.preview-hero__head {
  margin-bottom: var(--space-2);
}

.preview-hero__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 var(--space-2);
  border-radius: var(--radius-pill);
  font-size: var(--font-caption-size);
  font-weight: 700;
  border: 1px solid transparent;
  background: var(--bg-surface-raised);
}
.hero-ok .preview-hero__badge {
  color: var(--color-primary-700);
  border-color: var(--color-primary-300);
}
.hero-err .preview-hero__badge {
  color: var(--color-error-text);
  border-color: var(--color-error-border, #f4b5b5);
}

.preview-hero__total {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 4px;
}
.preview-hero__num {
  font-size: 32px;
  font-weight: 700;
  color: var(--color-primary-700);
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.preview-hero__unit {
  font-size: var(--font-body-size);
  color: var(--text-secondary);
  font-weight: 500;
}

.preview-hero__breakdown {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-small-size);
  color: var(--text-secondary);
}
.preview-hero__breakdown span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.preview-hero__dot {
  color: var(--border-strong);
}

.preview-hero__summary {
  margin: 0;
  color: var(--text-primary);
  font-size: var(--font-small-size);
}

/* 정보 그리드 — 2x2 */
.preview-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  background: var(--border-default);
  border-top: 1px solid var(--border-default);
  border-bottom: 1px solid var(--border-default);
}

.preview-info-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--space-3) var(--space-4);
  background: var(--bg-surface);
}

.preview-info-cell__label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-caption-size);
  color: var(--text-tertiary);
  font-weight: 600;
}

.preview-info-cell__value {
  font-size: var(--font-small-size);
  color: var(--text-primary);
  font-weight: 600;
  word-break: keep-all;
}

.mono {
  font-family: var(--font-mono-family, "JetBrains Mono", "D2Coding", monospace);
}

/* 학급 리스트 */
.preview-classes {
  background: var(--bg-surface);
}

.preview-classes__head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: var(--space-3) var(--space-4) var(--space-1);
  font-size: var(--font-caption-size);
  color: var(--text-tertiary);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.preview-classes__list {
  list-style: none;
  margin: 0;
  padding: 0 var(--space-4) var(--space-3);
}

.preview-class-row {
  display: grid;
  grid-template-columns: 56px 1fr 1fr;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-2) 0;
  font-size: var(--font-small-size);
  border-bottom: 1px solid var(--border-default);
}
.preview-class-row:last-child {
  border-bottom: none;
}

.preview-class-row__num {
  font-weight: 700;
  color: var(--color-primary-700);
  background: var(--color-primary-50);
  border-radius: var(--radius-sm);
  text-align: center;
  padding: 2px 6px;
  font-size: var(--font-caption-size);
}

.preview-class-row__count,
.preview-class-row__teacher {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--text-secondary);
  min-width: 0;
}
.preview-class-row__teacher {
  color: var(--text-tertiary);
  font-size: var(--font-caption-size);
}

/* 유저 정보 변경 — 변경 항목 리스트 */
.update-list {
  background: var(--bg-surface);
  border-top: 1px solid var(--border-default);
  border-bottom: 1px solid var(--border-default);
}
.update-list__head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: var(--space-3) var(--space-4) var(--space-1);
  font-size: var(--font-caption-size);
  color: var(--text-tertiary);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.update-list__items {
  list-style: none;
  margin: 0;
  padding: 0 var(--space-4) var(--space-3);
}
.update-row {
  display: grid;
  grid-template-columns: 72px 1fr auto 1fr;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-2) 0;
  font-size: var(--font-small-size);
  border-bottom: 1px solid var(--border-default);
}
.update-row:last-child {
  border-bottom: none;
}
.update-row__field {
  font-weight: 700;
  color: var(--color-primary-700);
  background: var(--color-primary-50);
  border-radius: var(--radius-sm);
  text-align: center;
  padding: 2px 6px;
  font-size: var(--font-caption-size);
}
.update-row__old {
  color: var(--text-tertiary);
  text-decoration: line-through;
  overflow: hidden;
  text-overflow: ellipsis;
}
.update-row__arrow {
  color: var(--text-tertiary);
}
.update-row__new {
  color: var(--color-primary-700);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 계약 기간 변경 — 반별 행 */
.contract-row {
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-3) 0;
  font-size: var(--font-small-size);
  border-bottom: 1px solid var(--border-default);
}
.contract-row:last-child {
  border-bottom: none;
}
.contract-row__class {
  font-weight: 700;
  color: var(--color-primary-700);
  background: var(--color-primary-50);
  border-radius: var(--radius-sm);
  text-align: center;
  padding: 4px 6px;
  font-size: var(--font-caption-size);
}
.contract-row__dates {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.contract-row__before,
.contract-row__after {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}
.contract-row__label {
  font-size: var(--font-caption-size);
  color: var(--text-tertiary);
  font-weight: 600;
  width: 48px;
}
.contract-row__before {
  color: var(--text-tertiary);
  text-decoration: line-through;
}
.contract-row__after {
  color: var(--color-primary-700);
  font-weight: 600;
}

.preview-warn,
.preview-error {
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-caption-size);
  border-top: 1px solid var(--border-default);
}
.preview-warn {
  background: #faedd3;
  color: #92560a;
}
.preview-error {
  background: #f8dee1;
  color: #b42318;
}
.preview-warn p,
.preview-error p {
  margin: 0 0 var(--space-1);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: var(--space-1);
}
.preview-warn ul,
.preview-error ul {
  margin: 0;
  padding-left: 18px;
}
.preview-warn ul li,
.preview-error ul li {
  margin: 2px 0;
}

.preview-actions {
  display: flex;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--bg-surface);
  border-top: 1px solid var(--border-default);
}

.btn-create {
  flex: 2 1 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  padding: 0 var(--space-4);
  background: var(--color-primary-600);
  color: var(--text-on-primary);
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-small-size);
  font-weight: 700;
  cursor: pointer;
  line-height: 1;
  white-space: nowrap;
  transition: background 0.15s, transform 0.05s;
}
.btn-create:hover:not(:disabled) {
  background: var(--color-primary-700);
}
.btn-create:active:not(:disabled) {
  transform: translateY(1px);
}
.btn-create:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel {
  flex: 1 1 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 var(--space-3);
  background: var(--bg-surface-raised);
  color: var(--text-secondary);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font-size: var(--font-small-size);
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-cancel:hover {
  background: var(--bg-subtle);
}

/* ── 결과 카드 ── */
.result-card {
  background: #e1f1e7;
  border: 1px solid #a8ddba;
  border-radius: var(--radius-md);
  padding: var(--space-4);
  font-size: var(--font-small-size);
}
.result-title {
  font-size: var(--font-h3-size);
  font-weight: var(--font-h3-weight);
  color: #1f7a3a;
  margin-bottom: var(--space-2);
}
.result-summary {
  margin: 0 0 var(--space-3);
  color: var(--text-primary);
}
.result-details summary {
  cursor: pointer;
  color: var(--color-primary-600);
  font-size: var(--font-small-size);
  padding: 2px 0;
}
.result-table-wrap {
  max-height: 220px;
  overflow-y: auto;
}

/* btn-csv: Primary → Secondary로 강등 (가이드 §5.2 화면당 Primary 1개) */
.btn-csv {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  margin-top: var(--space-3);
  padding: var(--space-2) var(--space-3);
  background: var(--bg-surface-raised);
  color: var(--text-primary);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font-size: var(--font-small-size);
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-csv:hover {
  background: var(--bg-subtle);
}

/* ── 입력창 ── */
.nlp-input-wrap {
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
  padding: var(--space-3) 0 0;
  flex-shrink: 0;
}

.nlp-input {
  flex: 1;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  font-size: var(--font-body-size);
  resize: none;
  outline: none;
  line-height: var(--font-body-line);
  transition: border-color 0.15s;
  background: var(--bg-surface-raised);
  color: var(--text-primary);
}
.nlp-input:focus {
  border-color: var(--color-primary-600);
}
.nlp-input:disabled {
  background: var(--bg-muted);
  color: var(--text-disabled);
}

.nlp-send-btn {
  width: 42px;
  height: 42px;
  border-radius: var(--radius-md);
  background: var(--color-primary-600);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s;
}
.nlp-send-btn:hover:not(:disabled) {
  background: var(--color-primary-700);
}
.nlp-send-btn:disabled {
  background: var(--bg-muted);
  cursor: not-allowed;
}
</style>
